/**
 * Web Audio API sound generators for match notifications.
 * No audio files needed — all sounds are synthesized in the browser.
 */

let audioCtx = null;

function getAudioContext() {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

export const SOUND_OPTIONS = [
  { id: 'match_confirm', name: 'Match Confirm' },
  { id: 'cs2_queue', name: 'CS2 Queue Pop' },
  { id: 'triple_beep', name: 'Triple Beep' },
  { id: 'ping', name: 'Ping' },
  { id: 'alert', name: 'Alert Sweep' },
  { id: 'chime', name: 'Chime' },
  { id: 'none', name: 'Inget ljud' },
];

function playTone(ctx, gain, freq, type, startTime, duration, fadeOut = true) {
  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);
  oscGain.gain.setValueAtTime(gain, startTime);
  if (fadeOut) {
    oscGain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  }
  osc.connect(oscGain);
  osc.start(startTime);
  osc.stop(startTime + duration);
  return { osc, oscGain };
}

const generators = {
  cs2_queue(ctx, masterGain) {
    // Rising two-tone, similar to CS2 queue pop
    const now = ctx.currentTime;
    const t1 = playTone(ctx, 0.4, 660, 'sine', now, 0.15, false);
    t1.oscGain.gain.setValueAtTime(0.4, now + 0.12);
    t1.oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    t1.oscGain.connect(masterGain);

    const t2 = playTone(ctx, 0.5, 880, 'sine', now + 0.18, 0.3);
    t2.oscGain.connect(masterGain);
  },

  triple_beep(ctx, masterGain) {
    const now = ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const t = playTone(ctx, 0.4, 1000, 'square', now + i * 0.18, 0.1);
      t.oscGain.connect(masterGain);
    }
  },

  ping(ctx, masterGain) {
    const now = ctx.currentTime;
    const t = playTone(ctx, 0.5, 1200, 'sine', now, 0.4);
    t.oscGain.connect(masterGain);
  },

  alert(ctx, masterGain) {
    // Upward frequency sweep
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.4);
    oscGain.gain.setValueAtTime(0.3, now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc.connect(oscGain);
    oscGain.connect(masterGain);
    osc.start(now);
    osc.stop(now + 0.5);
  },

  chime(ctx, masterGain) {
    const now = ctx.currentTime;
    const t1 = playTone(ctx, 0.3, 523, 'sine', now, 0.5);
    t1.oscGain.connect(masterGain);
    const t2 = playTone(ctx, 0.4, 784, 'sine', now + 0.15, 0.6);
    t2.oscGain.connect(masterGain);
  },
};

// Audio file cache
const audioCache = {};

function playAudioFile(filename, volume) {
  const basePath = process.env.PUBLIC_URL || '';
  const url = `${basePath}/sounds/${filename}`;
  if (!audioCache[url]) {
    audioCache[url] = new Audio(url);
  }
  const audio = audioCache[url];
  audio.volume = volume / 100;
  audio.currentTime = 0;
  audio.play().catch(() => {});
}

// Map sound IDs to audio files (null = use Web Audio generator)
const audioFiles = {
  match_confirm: 'match_confirm.mp3',
};

/**
 * Play a notification sound.
 * @param {string} soundId - One of the SOUND_OPTIONS ids
 * @param {number} volume - 0 to 100
 */
export function playSound(soundId, volume = 70) {
  if (soundId === 'none') return;

  try {
    // Check for audio file first
    if (audioFiles[soundId]) {
      playAudioFile(audioFiles[soundId], volume);
      return;
    }
    // Fall back to Web Audio generator
    if (!generators[soundId]) return;
    const ctx = getAudioContext();
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(volume / 100, ctx.currentTime);
    masterGain.connect(ctx.destination);
    generators[soundId](ctx, masterGain);
  } catch (e) {
    console.warn('Could not play sound:', e);
  }
}
